import { useState, useEffect, useCallback, useRef } from 'react';
import { accountsApi } from '../services/api/endpoints/accounts';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import type { Account } from '../types/accounting';
import type { AccountSearchParams, AccountFilters } from '../types/filters';

export function useAccounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<AccountFilters>({});
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  const { currentFiscalYear } = useFiscalYearStore();
  
  // Ref pour éviter les appels multiples
  const isLoadingRef = useRef(false);
  const lastSearchParamsRef = useRef<string>('');

  // Fonction de chargement mémoïsée
  const loadAccounts = useCallback(async (searchParams?: AccountSearchParams) => {
    // Éviter les appels dupliqués avec les mêmes paramètres
    const paramsKey = JSON.stringify({ ...searchParams, fiscalYear: currentFiscalYear?.id });
    if (isLoadingRef.current && paramsKey === lastSearchParamsRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    lastSearchParamsRef.current = paramsKey;
    
    // Détecter si c'est une navigation de page (vs chargement initial / changement FY)
    const isPageNavigation = searchParams?.page !== undefined;
    
    try {
      setLoading(true);
      setError(null);
      
      // ⚡ Vider les données uniquement lors d'un changement de contexte (FY/BU),
      // pas lors de la navigation entre pages (évite le flash et la disparition de la pagination)
      if (!isPageNavigation) {
        setAccounts([]);
        setFilteredAccounts([]);
      }
      
      // Ajouter pagination par défaut + filtres d'unité commerciale et exercice fiscal
      const paramsWithFilters: AccountSearchParams = {
        page: 1,
        pageSize: 20,
        ...searchParams,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType,
        fiscalYear: currentFiscalYear?.id
      };
      
      // Load from IndexedDB first (seulement sur chargement initial, pas navigation de page)
      if (!isPageNavigation && !searchParams?.search) {
        const localAccounts = await IndexedDBService.getAll<Account>('accounts');
        if (localAccounts.length > 0) {
          const filtered = applyFiltersInternal(localAccounts, paramsWithFilters);
          setAccounts(localAccounts);
          setFilteredAccounts(filtered.data);
          setPagination(filtered.pagination);
        }
      }

      // If online, fetch from API and update local storage
      if (navigator.onLine) {
        console.log(`📡 Chargement API comptes pour FY: ${currentFiscalYear?.id || 'tous'}, page: ${paramsWithFilters.page}/${paramsWithFilters.pageSize}`);
        const response = await accountsApi.getAll(paramsWithFilters);
        if (response.success && response.data) {
          setAccounts(response.data.data);
          setFilteredAccounts(response.data.data);
          setPagination({
            page: response.data.page,
            pageSize: response.data.pageSize,
            total: response.data.total,
            totalPages: response.data.totalPages
          });
          
          // Update IndexedDB (accumuler les comptes pour le mode hors-ligne)
          if (!searchParams?.search) {
            for (const account of response.data.data) {
              await IndexedDBService.update('accounts', account);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Impossible de charger les comptes');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [effectiveFilter.businessUnitId, effectiveFilter.businessUnitType, currentFiscalYear?.id]);

  // Charger au montage et quand les dépendances changent
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Fonction interne pour appliquer les filtres (non exportée)
  const applyFiltersInternal = (accountsList: Account[], searchParams?: AccountSearchParams) => {
    let filtered = [...accountsList];
    
    if (searchParams?.search) {
      const search = searchParams.search.toLowerCase();
      filtered = filtered.filter(account => 
        account.code.toLowerCase().includes(search) ||
        account.name.toLowerCase().includes(search)
      );
    }
    
    if (searchParams?.type && searchParams.type !== 'all') {
      filtered = filtered.filter(account => account.type === searchParams.type);
    }
    
    if (searchParams?.standard && searchParams.standard !== 'all') {
      filtered = filtered.filter(account => account.standard === searchParams.standard);
    }
    
    if (searchParams?.isAnalytic !== undefined) {
      filtered = filtered.filter(account => account.isAnalytic === searchParams.isAnalytic);
    }
    
    // Filtrer par unité commerciale côté client si nécessaire
    if (searchParams?.businessUnitId) {
      filtered = filtered.filter(account => 
        (account as any).businessUnitId === searchParams.businessUnitId ||
        !(account as any).businessUnitId // Comptes globaux accessibles à tous
      );
    }
    
    // Sorting
    if (searchParams?.sortBy) {
      filtered.sort((a, b) => {
        const order = searchParams.sortOrder === 'desc' ? -1 : 1;
        if (searchParams.sortBy === 'code') {
          return a.code.localeCompare(b.code) * order;
        } else if (searchParams.sortBy === 'name') {
          return a.name.localeCompare(b.name) * order;
        } else if (searchParams.sortBy === 'type') {
          return a.type.localeCompare(b.type) * order;
        }
        return 0;
      });
    }
    
    // Pagination
    const page = searchParams?.page || 1;
    const pageSize = searchParams?.pageSize || 20;
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      data: filtered.slice(startIndex, endIndex),
      pagination: {
        page,
        pageSize,
        total,
        totalPages
      }
    };
  };

  const applyLocalFilters = useCallback((newFilters: AccountFilters) => {
    setFilters(newFilters);
    const searchParams: AccountSearchParams = {
      ...newFilters,
      page: 1, // Reset to first page when filters change
      pageSize: pagination.pageSize
    };
    loadAccounts(searchParams);
  }, [pagination.pageSize]);

  const addAccount = async (data: Partial<Account>): Promise<Account> => {
    // Validation des données requises
    if (!data.code || !data.name || !data.type) {
      throw new Error('Code, nom et type sont requis');
    }

    // Récupérer les IDs requis pour l'API
    const companyId = (data as any).companyId || selectedBusinessUnit?.id || 'default-company';
    const fiscalYearId = (data as any).fiscalYearId || currentFiscalYear?.id || '';

    // Créer l'objet Account avec les champs requis
    const newAccount: Account = {
      id: crypto.randomUUID(),
      code: data.code,
      name: data.name,
      type: data.type,
      standard: data.standard || 'SYSCOHADA',
      isAnalytic: data.isAnalytic || false,
      description: data.description,
      active: data.active ?? true,
      companyId,
      fiscalYearId,
      class: data.code.charAt(0), // Classe SYSCOHADA basée sur le 1er chiffre
      parentId: data.parentId,
      businessUnitId: data.businessUnitId || selectedBusinessUnit?.id,
      businessUnitType: data.businessUnitType || selectedBusinessUnit?.type,
      metadata: data.metadata
    };

    // Données pour l'API (tous les champs requis)
    const apiData = {
      code: newAccount.code,
      name: newAccount.name,
      type: newAccount.type,
      companyId,
      fiscalYearId,
      class: newAccount.class,
      standard: newAccount.standard,
      parentId: newAccount.parentId,
      isAnalytic: newAccount.isAnalytic,
      description: newAccount.description,
      active: newAccount.active,
      businessUnitId: newAccount.businessUnitId,
      businessUnitType: newAccount.businessUnitType,
      metadata: newAccount.metadata
    };

    // Save to IndexedDB first for offline support
    await IndexedDBService.add('accounts', newAccount);
    setAccounts(prev => [...prev, newAccount]);
    setFilteredAccounts(prev => [...prev, newAccount]);

    // Try to sync with API if online
    if (navigator.onLine) {
      try {
        const response = await accountsApi.create(apiData);
        if (response.success && response.data) {
          // Update local storage with server response
          await IndexedDBService.update('accounts', response.data);
          setAccounts(prev => prev.map(acc => acc.id === newAccount.id ? response.data! : acc));
          setFilteredAccounts(prev => prev.map(acc => acc.id === newAccount.id ? response.data! : acc));
          return response.data;
        }
      } catch (error) {
        console.error('Error creating account on server:', error);
        // Keep local version, add to sync queue
        await SyncService.addToSyncQueue({
          id: newAccount.id,
          type: 'CREATE',
          entity: 'accounts',
          data: apiData
        });
      }
    } else {
      // Add to sync queue for later
      await SyncService.addToSyncQueue({
        id: newAccount.id,
        type: 'CREATE',
        entity: 'accounts',
        data: apiData
      });
    }

    return newAccount;
  };

  const updateAccount = async (id: string, data: Partial<Account>): Promise<Account> => {
    const existingAccount = accounts.find(acc => acc.id === id);
    if (!existingAccount) {
      throw new Error('Account not found');
    }

    const updatedAccount: Account = { ...existingAccount, ...data };

    // Update local storage immediately
    await IndexedDBService.update('accounts', updatedAccount);
    
    // Update local state
    const updatedAccounts = accounts.map(acc => 
      acc.id === id ? updatedAccount : acc
    );
    setAccounts(updatedAccounts);
    setFilteredAccounts(updatedAccounts);

    // Try to sync with API if online
    if (navigator.onLine) {
      try {
        const response = await accountsApi.update(id, data);
        if (response.success && response.data) {
          return response.data;
        }
      } catch (error) {
        console.error('Error updating account:', error);
      }
    }

    return updatedAccount;
  };

  const deleteAccount = async (id: string): Promise<void> => {
    // Remove from local storage immediately
    await IndexedDBService.delete('accounts', id);
    
    // Update local state
    const updatedAccounts = accounts.filter(acc => acc.id !== id);
    setAccounts(updatedAccounts);
    setFilteredAccounts(updatedAccounts);

    // Try to sync with API if online
    if (navigator.onLine) {
      try {
        await accountsApi.delete(id);
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }
  };

  return {
    accounts: filteredAccounts,
    allAccounts: accounts,
    loading,
    error,
    pagination,
    filters,
    addAccount,
    updateAccount,
    deleteAccount,
    loadAccounts,
    applyFilters: applyLocalFilters,
    // Exposer les infos de filtre actuels
    currentFilters: {
      fiscalYearId: currentFiscalYear?.id,
      businessUnitId: selectedBusinessUnit?.id,
      businessUnitType: selectedBusinessUnit?.type
    }
  };
}